import { defineStore } from 'pinia';
import { DEFAULT_DOMAIN_NAME } from '@/common/constants/domains.constants';
import { pick, uniq } from 'lodash-es';
import { computed, ref } from 'vue';
import { store } from '@/common/root';
import NotFoundError from '@/common/utils/errors/NotFoundError';
import { DOMAIN_PROPS_ACCESS_TYPES } from '@/common/constants/domainProps.constants';
import THEMES_LIST from '@/common/constants/themes.constants';
import loadUikitColorScheme from '@/common/utils/loadColorScheme';
import hciThemeData from '@/hci/config/themes';
import ssThemeData from '@/self-service/config/themes';
import isSelfService from '@/common/utils/isSelfService';
import useAppModeStore from '@/common/store/pinia/appMode';

/* global APP_THEME */
export const DEFAULT_COLOR_SCHEME = APP_THEME === 'virtuozzo' ? 'virtuozzo' : 'default';
export const GENERAL_THEME_KEY = 'GeneralTheme';

const defaultThemeData = isSelfService() ? ssThemeData[APP_THEME] : hciThemeData[APP_THEME];

const allowThemeVariables = [
  'productTitle',
  'colorScheme',
  'logo',
  'loginLogo',
  'favicon'
];

export type ThemeScheme = {
  productTitle?: string
  colorScheme?: string
  logo?: string
  loginLogo?: string
  favicon?: string,
  pristine?: boolean
  cssVariables?: object
}

const availableSchemes = [
  DEFAULT_COLOR_SCHEME,
  ...THEMES_LIST
];

export const useThemeStore = defineStore('theme', () => {
  const themeName = ref(APP_THEME);
  const schemesState = ref({});
  const themeLoaded = ref(false);

  const generalTheme = computed<ThemeScheme>(() => store.getters['domainProps/getDomainProp'](DEFAULT_DOMAIN_NAME)('GeneralTheme') || {});
  const getTheme = computed<(domainName?: string) => ThemeScheme>(() => (domainName?: string) => {
    if (!domainName || !store.getters['domainProps/getDomainProp'](domainName)('theme')) {
      return generalTheme.value;
    }
    return store.getters['domainProps/getDomainProp'](domainName)('theme');
  });
  const getThemedDomains = computed<string[]>(() => {
    const domains = store.getters['domains/domains/getProps']
      .filter((prop) => prop.keys.includes('theme'))
      .map((prop) => prop.domain);
    return uniq(domains);
  });
  const themeData = computed(function () {
    const appModeStore = useAppModeStore();
    if (isSelfService()) {
      return {
        ...defaultThemeData,
        ...generalTheme.value,
        ...store.getters['domainProps/getCurrentDomainProp']('theme'),
        ...appModeStore.themeOverrides
      };
    }
    return {
      ...defaultThemeData,
      ...appModeStore.themeOverrides
    };
  });
  const fetchSchemeData = async (name: string): Promise<Record<string, string> | null> => {
    const scheme = name ? await loadUikitColorScheme(name) : null;

    // copy variables for disable reactivity in import cache
    const variables = scheme ? { ...scheme } : null;
    schemesState.value[name] = variables;

    return variables;
  };

  const fetchAvailableSchemes = () => Promise.all(availableSchemes.map(name => fetchSchemeData(name)));

  const fetchGeneralTheme = async () => {
    try {
      const theme: ThemeScheme = await store.dispatch('domainProps/FETCH_PROP', {
        key: GENERAL_THEME_KEY,
        domain: DEFAULT_DOMAIN_NAME
      });
      if (theme?.colorScheme) {
        await fetchSchemeData(theme.colorScheme);
      }
    } catch (error) {
      if (!(error instanceof NotFoundError)) {
        throw error;
      }
    }
  };

  const fetchTheme = async (domainName: string) => {
    themeLoaded.value = false;
    try {
      const theme: ThemeScheme = await store.dispatch('domainProps/FETCH_PROP', {
        key: 'theme',
        domain: domainName
      });
      if (theme?.colorScheme) {
        await fetchSchemeData(theme.colorScheme);
      }
    } catch (error) {
      try {
        await fetchGeneralTheme();
      } catch (innerError) {
        if (!(innerError instanceof NotFoundError)) {
          throw innerError;
        }
      }
    } finally {
      themeLoaded.value = true;
    }
  };

  const createTheme = (data: ThemeScheme, domainName?: string) => {
    const theme = { ...getTheme.value(domainName), ...data };
    delete theme.pristine;
    const payload = pick(theme, allowThemeVariables);

    if (domainName) {
      return store.dispatch('domainProps/SET', {
        domain: domainName,
        key: 'theme',
        value: payload,
        access: DOMAIN_PROPS_ACCESS_TYPES.PUBLIC
      });
    }
    return store.dispatch('domainProps/SET', {
      domain: DEFAULT_DOMAIN_NAME,
      key: GENERAL_THEME_KEY,
      value: payload,
      access: DOMAIN_PROPS_ACCESS_TYPES.PUBLIC
    });
  };

  const updateTheme = (data: ThemeScheme, domainName?: string, isDefault?: boolean) => {
    const theme = { ...getTheme.value(domainName), ...data };
    if (isDefault) {
      return createTheme(data, domainName);
    }
    const payload = pick(theme, allowThemeVariables);
    if (domainName) {
      return store.dispatch('domainProps/SET', {
        domain: domainName,
        key: 'theme',
        value: payload,
        access: DOMAIN_PROPS_ACCESS_TYPES.PUBLIC
      });
    }
    return store.dispatch('domainProps/SET', {
      domain: DEFAULT_DOMAIN_NAME,
      key: GENERAL_THEME_KEY,
      value: payload,
      access: DOMAIN_PROPS_ACCESS_TYPES.PUBLIC
    });
  };

  const updateColorScheme = async (domainName: string, scheme: string) => {
    const variables = await fetchSchemeData(scheme);
    return updateTheme({
      colorScheme: scheme,
      cssVariables: variables ?? undefined
    }, domainName);
  };

  const deleteTheme = async (domainName?: string) => {
    if (!domainName) {
      return store.dispatch('domainProps/DELETE_PROP', {
        domain: DEFAULT_DOMAIN_NAME,
        key: GENERAL_THEME_KEY
      });
    }

    return store.dispatch('domainProps/DELETE_PROP', {
      domain: domainName,
      key: 'theme'
    });
  };

  return {
    themeName,
    schemesState,
    themeLoaded,
    generalTheme,
    getTheme,
    getThemedDomains,
    themeData,
    fetchSchemeData,
    fetchAvailableSchemes,
    fetchGeneralTheme,
    fetchTheme,
    createTheme,
    updateTheme,
    updateColorScheme,
    deleteTheme
  };
});

export default useThemeStore;
